import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { SkillService } from 'src/app/common/services/skill/skill.service';
import { markAllControlsAsDirty } from 'src/app/form-helper.ts';
import { ButtonColorEnum } from 'src/app/shared/buttons/enums/button-color.enum';
import { InputTypeEnum } from 'src/app/shared/input/enums/input-type.enum';
import { MaskTypeEnum } from 'src/app/shared/input/enums/mask-type.enum';
import {
  CreateUpdateClientRequest,
  GetSkillRequest,
  GetSkillResponse,
  Shift,
} from '../models/client.model';
import { ClientService } from '../service/client.service';

@Component({
  selector: 'app-client-details',
  templateUrl: './client-details.component.html',
  styleUrls: ['./client-details.component.scss'],
})
export class ClientDetailsComponent implements OnInit {
  form = this.formBuilder.group({
    name: ['', Validators.required],
    active: ['', Validators.required],
    cnpj: ['', Validators.required],
    skill: [['']],
    holdingLineTime: [''],
    readinessTimeExceeded: [''],
    returnTimeExceeded: [''],
    minimumCallTime: [''],
  });

  skills: string[] = [];
  selectedSkills: string[] = [];
  isLoading: boolean = false;
  status: string[] = ['Ativo', 'Inativo'];
  selectedStatus: string[] = [];
  error: boolean = false;
  clientId: string = '';
  originalSkills: GetSkillResponse[] = [];
  totalItemsSkills: number = 0;
  loadingMoreSkills: boolean = false;
  loadingSearchSkills: boolean = false;
  loadingName: boolean = false;
  loadingSkills: boolean = false;
  loadingCNPJ: boolean = false;
  loadingStatus: boolean = false;
  loadingHoldingLineTime: boolean = false;
  loadingReadinessTimeExceeded: boolean = false;
  loadingReturnTimeExceeded: boolean = false;
  loadingMinimumCallTime: boolean = false;
  cnpjErrorMessage: string = '';
  ButtonColorEnum = ButtonColorEnum;
  InputTypeEnum = InputTypeEnum;
  MaskTypeEnum = MaskTypeEnum;
  errorMessageStartTime: string = '';
  errorMessageEndTime: string = '';
  showShiftErrorMessage: boolean = true;
  shiftIsOpen: boolean = false;
  shiftInputs: Array<any> = [];
  shifts: string[] = ['Manhã', 'Tarde', 'Noite', 'Madrugada'];
  maxShifts = this.shifts.length;
  loadingShifts: boolean = false;

  skillFilter: GetSkillRequest = {
    pageSize: 10,
    pageNumber: 1,
    active: true,
    sortAscending: true,
    sortBy: 'Code',
  } as GetSkillRequest;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private clientService: ClientService,
    private route: ActivatedRoute,
    private skillService: SkillService,
    private toastrService: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadingSkills = true;
    this.loadingName = true;
    this.loadingCNPJ = true;
    this.loadingStatus = true;
    this.loadingHoldingLineTime = true;
    this.loadingReadinessTimeExceeded = true;
    this.loadingReturnTimeExceeded = true;
    this.loadingMinimumCallTime = true;
    this.loadingShifts = true;

    this.route.params.subscribe((params) => {
      const id = params['id'];
      if (id) this.clientId = id;
    });

    this.getSkills();
    this.populateClient();
  }

  addShiftInput(
    shiftName: string | null = null,
    startTime: string | null = null,
    endTime: string | null = null
  ) {
    const shiftGroup = this.formBuilder.group({
      shiftName: [shiftName, Validators.required],
      startTime: [startTime, Validators.required],
      endTime: [endTime, Validators.required],
    });

    markAllControlsAsDirty(shiftGroup);

    this.shiftInputs.push(shiftGroup);
    this.shiftIsOpen = true;
  }

  handleToggleAccordion() {
    this.shiftIsOpen = !this.shiftIsOpen;
  }

  handleShiftNameChange(event: any) {
    if (!event.old && event.current) {
      this.shifts = this.shifts.filter((i) => i !== event.current);
      this.showShiftErrorMessage = false;
    } else if (!event.current && event.old) {
      this.shifts.push(event.old);
      this.showShiftErrorMessage = true;
    } else if (event.current && event.old && event.current != event.old) {
      this.shifts = this.shifts.filter((i) => i !== event.current);
      this.shifts.push(event.old);
    }
  }

  removeShiftInput(index: number) {
    if (this.shiftInputs[index].value.shiftName)
      this.shifts.push(this.shiftInputs[index].value.shiftName);
    this.shiftInputs.splice(index, 1);
    if (this.shiftInputs.length === 0) {
      this.shiftIsOpen = false;
    }
  }

  getErrorStartTime(shiftGroup: any, indexToRemove: number): boolean {
    if (
      !shiftGroup.controls.startTime.value &&
      !shiftGroup.controls.startTime.valid
    ) {
      this.errorMessageStartTime = 'Tempo inválido.';
      return true;
    } else {
      const otherShifts = this.shiftInputs.filter(
        (_, index) => index !== indexToRemove
      );
      const currentShift = this.shiftInputs[indexToRemove];
      const currentStartTime = this.convertTimeToDate(
        currentShift.controls.startTime.value
      );

      for (let i of otherShifts) {
        const startTime = this.convertTimeToDate(i.controls.startTime.value);
        const endTime = this.convertTimeToDate(i.controls.endTime.value);

        if (startTime && endTime && currentStartTime) {
          if (currentStartTime >= startTime && currentStartTime <= endTime) {
            const usedShift = i.controls.shiftName.value;

            if (usedShift) {
              this.errorMessageStartTime = `Esta hora já está sendo utilizada no turno '${usedShift}'.`;
            } else {
              this.errorMessageStartTime =
                'Esta hora já está sendo utilizada em outro turno.';
            }
            return true;
          }
        }
      }

      return false;
    }
  }

  getErrorEndTime(shiftGroup: any, indexToRemove: number): boolean {
    if (!shiftGroup.controls.endTime.value) {
      this.errorMessageEndTime = 'Tempo inválido.';
      return true;
    } else {
      const otherShifts = this.shiftInputs.filter(
        (_, index) => index !== indexToRemove
      );

      const currentShift = this.shiftInputs[indexToRemove];
      const currentEndTime = this.convertTimeToDate(
        currentShift.controls.endTime.value
      );

      for (let i of otherShifts) {
        const startTime = this.convertTimeToDate(i.controls.startTime.value);
        const endTime = this.convertTimeToDate(i.controls.endTime.value);

        if (startTime && endTime && currentEndTime) {
          if (currentEndTime >= startTime && currentEndTime <= endTime) {
            const usedShift = i.controls.shiftName.value;

            if (usedShift) {
              this.errorMessageEndTime = `Esta hora já está sendo utilizada no turno '${usedShift}'.`;
            } else {
              this.errorMessageEndTime =
                'Esta hora já está sendo utilizada em outro turno.';
            }
            return true;
          }
        }
      }
      return false;
    }
  }

  convertTimeToDate(timeString: string): Date | null {
    if (timeString) {
      const [hours, minutes] = timeString.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      return date;
    }
    return null;
  }

  handleCNPJValueChanged(event: any) {
    this.cnpjErrorMessage = event
      ? event.length === 0
        ? 'campo obrigatório'
        : 'CNPJ incompleto.'
      : 'campo obrigatório';
  }

  clear() {
    this.form.reset();
    this.selectedSkills = [];
  }

  handleNavigateToList() {
    this.router.navigate(['client']);
  }

  getSkillIdFromText(text: string[]): string[] | null {
    if (!text || text.length === 0) return null;

    const skills: string[] = [];

    for (let i = 0; i < text.length; i++) {
      if (!text[i] || text[i].trim() === '') return null;

      const [code, cellAndSubCell] = text[i].split(':').map((i) => i.trim());
      const [cell, subCell] = cellAndSubCell.split('—').map((i) => i.trim());

      const skillFiltered = this.originalSkills.find(
        (i) =>
          i.code.toString() === code &&
          i.cell.trim() === cell &&
          i.subCell.trim() === subCell
      );

      if (!skillFiltered) return null;

      skills.push(skillFiltered.id);
    }

    return skills;
  }

  getSkills() {
    this.skillFilter.pageNumber = 1;
    this.loadingSkills = true;

    this.skillService.get(this.skillFilter).subscribe({
      next: (data) => {
        this.originalSkills = data.items.filter((s) => s.client == null);

        this.skills = this.originalSkills.map(
          (i) => i.code + ': ' + i.cell + ' — ' + i.subCell
        );

        this.totalItemsSkills = data.totalItems;
        this.loadingSkills = false;
      },
      error: () => {
        this.toastrService.error('Não foi possível carregar as skills.');
        this.loadingSkills = false;
      },
    });
  }

  getMoreSkills() {
    if (this.totalItemsSkills != this.skills.length) {
      this.loadingMoreSkills = true;
      if (this.skillFilter.pageNumber) this.skillFilter.pageNumber += 1;

      this.skillService.get(this.skillFilter).subscribe({
        next: (data) => {
          this.originalSkills = this.originalSkills.concat(
            data.items.filter((s) => s.client == null)
          );
          this.totalItemsSkills = data.totalItems;

          this.skills = this.skills.concat(
            this.originalSkills.map(
              (i) => i.code + ': ' + i.cell + ' — ' + i.subCell
            )
          );

          this.loadingMoreSkills = false;
        },
        error: () => {
          this.toastrService.error('Não foi possível carregar as skills.');
          this.loadingMoreSkills = false;
        },
      });
    }
  }

  populateClient() {
    this.clientService.getById(this.clientId).subscribe((client) => {
      if (client) {
        this.selectedSkills = client.skills.map(
          (i) => i.code + ': ' + i.cell + ' — ' + i.subCell
        );
        this.loadingSkills = false;
        this.form.patchValue({
          name: client.name,
          cnpj: client.cnpj,
          skill: this.selectedSkills,
          active: client.active ? 'Ativo' : 'Inativo',
          holdingLineTime:
            client.holdingLineTime != null
              ? client.holdingLineTime.replace(':', '').replace(':', '')
              : null,
          readinessTimeExceeded:
            client.readinessTimeExceeded != null
              ? client.readinessTimeExceeded.replace(':', '').replace(':', '')
              : null,
          returnTimeExceeded:
            client.returnTimeExceeded != null
              ? client.returnTimeExceeded.replace(':', '').replace(':', '')
              : null,
          minimumCallTime:
            client.minimumCallTime != null
              ? client.minimumCallTime.replace(':', '').replace(':', '')
              : null,
        });

        if (client.shifts) {
          client.shifts.forEach((i) => {
            const formattedStartTime = i.startTime
              ? i.startTime.substring(0, 5)
              : null;
            const formattedEndTime = i.endTime
              ? i.endTime.substring(0, 5)
              : null;
            this.addShiftInput(i.name, formattedStartTime, formattedEndTime);
          });
        }

        this.selectedStatus = client.active ? ['Ativo'] : ['Inativo'];
        this.loadingCNPJ = false;
        this.loadingStatus = false;
        this.loadingName = false;
        this.loadingHoldingLineTime = false;
        this.loadingReadinessTimeExceeded = false;
        this.loadingReturnTimeExceeded = false;
        this.loadingMinimumCallTime = false;
        this.loadingShifts = false;
      }
    });
  }

  stringToTime(timeString: string | null): Date | null | string {
    if (timeString == null || timeString == '') return null;

    timeString = timeString.trim();

    if (timeString.length !== 6) {
      this.toastrService.error(
        'Tempo "' + timeString + '" inválido, esperado 6 dígitos.'
      );
      return 'error';
    }

    const hours = parseInt(timeString.substring(0, 2), 10);
    const minutes = parseInt(timeString.substring(2, 4), 10);
    const seconds = parseInt(timeString.substring(4, 6), 10);

    if (
      isNaN(hours) ||
      isNaN(minutes) ||
      isNaN(seconds) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59 ||
      seconds < 0 ||
      seconds > 59
    ) {
      this.toastrService.error(
        'Tempo "' +
          timeString +
          '" inválido. Horas, minutos ou segundos fora do intervalo válido.'
      );
      return 'error';
    }

    const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

    return formattedTime;
  }

  update() {
    if (!this.form.valid || this.isLoading) return;

    this.isLoading = true;
    this.error = false;

    const skills = this.form.controls.skill.value ?? [];
    let finalSkills = null;

    if (!skills || skills.length > 0) {
      if (skills.length != 1 || skills[0].trim() != '') {
        finalSkills = this.getSkillIdFromText(skills);

        if (!finalSkills || finalSkills.length !== skills.length) {
          this.isLoading = false;
          this.error = true;
          this.toastrService.error(
            'Ocorreu um erro ao recuperar skill selecionada'
          );
          return;
        }
      }
    }

    const holdingLineTime = this.stringToTime(
      this.form.controls.holdingLineTime.value
    );

    const readinessTimeExceeded = this.stringToTime(
      this.form.controls.readinessTimeExceeded.value
    );

    const returnTimeExceeded = this.stringToTime(
      this.form.controls.returnTimeExceeded.value
    );

    const minimumCallTime = this.stringToTime(
      this.form.controls.minimumCallTime.value
    );

    if (
      holdingLineTime == 'error' ||
      readinessTimeExceeded == 'error' ||
      returnTimeExceeded == 'error' ||
      minimumCallTime == 'error'
    ) {
      this.error = true;
      this.isLoading = false;
      return;
    }

    const shifts: Shift[] = [];

    this.shiftInputs.forEach((i) => {
      const newShift: Shift = {
        name: i.value.shiftName,
        startTime: i.value.startTime + ':00',
        endTime: i.value.endTime + ':59',
      };
      shifts.push(newShift);
    });

    const request = {
      name: this.form.controls.name.value ?? '',
      active: this.form.controls.active.value === 'Ativo',
      skills: finalSkills,
      cnpj: this.form.controls.cnpj.value ?? '',
      holdingLineTime: holdingLineTime,
      readinessTimeExceeded: readinessTimeExceeded,
      returnTimeExceeded: returnTimeExceeded,
      minimumCallTime: minimumCallTime,
      shifts: shifts.length === 0 ? null : shifts,
    } as CreateUpdateClientRequest;

    this.clientService.update(this.clientId, request).subscribe({
      next: () => {
        this.isLoading = false;
        this.toastrService.success('Cliente alterado com sucesso!');
        this.router.navigate(['/client']);
      },
      error: (e) => {
        this.isLoading = false;
        this.error = true;
        this.toastrService.error('Ocorreu um erro ao atualizar cliente.');
      },
    });
  }

  getShiftsValid(): boolean {
    return this.shiftInputs.every((i) => i.status == 'VALID');
  }
}
